import React, { useState, useEffect, useCallback } from "react";
import I18n from "i18n";
import vent from "libraries/vent";

// Utility for keyboard shortcuts
function useShortcut(keys: string[], handler: () => void) {
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (
        (keys.includes("!") && e.key === "!") ||
        (keys.includes("0") && e.key === "0")
      ) {
        handler();
      }
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [keys, handler]);
}

interface Props {
  important: boolean;
  selectedAgentResults: any; // Backbone.Model
  disabled?: boolean;
}

export default function DetailViewImportantBtn({
  important = false,
  selectedAgentResults,
  disabled = false,
}: Props) {
  const [isActive, setIsActive] = useState(important);
  const [isSaving, setIsSaving] = useState(false);

  const agentResults = selectedAgentResults?.collection?.models || [];

  useEffect(() => {
    setIsActive(important);
  }, [important]);

  const saveImportantValue = useCallback(
    async (newValue: boolean) => {
      setIsSaving(true);
      let success = true;
      try {
        // Store previous model values in case update fails
        agentResults.forEach((agentResult: any) => agentResult.store && agentResult.store());

        agentResults.forEach((agentResult: any) => agentResult.set && agentResult.set('important', newValue));
        selectedAgentResults.set && selectedAgentResults.set('important', newValue);

        await new Promise((resolve, reject) => {
          selectedAgentResults.save(
            { agent_result: { important: newValue } },
            {
              patch: true,
              success: resolve,
              error: reject,
            }
          );
        });

        vent.trigger(
          "changed:resultImportance",
          agentResults.map((r: any) => (r.get ? r.get('id') : r.id)),
          newValue
        );
      } catch {
        agentResults.forEach((agentResult: any) => agentResult.restore && agentResult.restore());
        setIsActive(important);
        success = false;
      }
      setIsSaving(false);
      return success;
    },
    [important, selectedAgentResults, agentResults]
  );

  const handleClick = async () => {
    if (isSaving || disabled) return;
    const newValue = !isActive;
    setIsActive(newValue);
    await saveImportantValue(newValue);
  };

  const shortcutHandler = useCallback(() => {
    if (!isSaving && !disabled) {
      handleClick();
    }
  }, [isSaving, disabled, handleClick]);

  useShortcut(["!", "0"], shortcutHandler);

  return (
    <div className="clip-actions__highlighting mrm">
      <a
        data-mark-important
        className={
          "button button--small button--block button--toggle" +
          (isActive ? " is-active" : "") +
          (isSaving ? " is-saving" : "")
        }
        onClick={handleClick}
        aria-disabled={isSaving || disabled}
        style={isSaving || disabled ? { pointerEvents: "none", opacity: 0.6 } : {}}
      >
        <i className="clip-actions__highlighting-icon"></i>{" "}
        {I18n.t("webapp.agents.results.result_details.important")}
      </a>
    </div>
  );
}